import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarClock, Eye, EyeOff, Flag, MessageCircle, Pin, Play, Scissors, Send, Trash2 } from "lucide-react";
import { CHELSEA, CLASSES, FOUNDING, PROJECTS, countComments, hasReplay, viewer, type Member } from "./sample";
import { JoinButton, M, MembersPage, VisBadge, useRole } from "./Members";
import { Part } from "../Page";
import { Draw, EASE, HEART, Head } from "../bits";
import {
  Avatar, EditForm, KIND, LikeButton, Media, PostLocked, PostMenu, ShareButton, SideCard, ToastHost,
  canSee, mapComment, postStore, published, toast, toggleLike, useHref, usePosts,
  type LiveComment, type LivePost,
} from "./PostCard";
import { Calendar, RoleCard, fmtWhen } from "./Posts";
import "./Posts.css";
import "./Post.css";

/* One club post (/blue-2/members/posts/:id): the full post, threaded comments
   with replies (Chelsea's highlighted), comment likes, reporting, and for
   Chelsea, hide/report moderation and the post's pin / edit / delete. Locked
   for roles that can't see it. Local state only, shared with the feed. */

/* ─── Header ─── */

function Header({ post }: { post?: LivePost }) {
  const href = useHref();
  return <div className="mpd-head">
    <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: EASE }}>
      <Link className="mpd-back" to={href(M.posts)}><ArrowLeft size={16} aria-hidden="true" /> All posts</Link>
    </motion.div>
    {post ? <>
      <motion.p className="mpd-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
        {KIND[post.kind]}{post.pinned && <span className="mpd-kicker-pin"><Pin size={12} aria-hidden="true" /> pinned</span>}
      </motion.p>
      <motion.h1 className="mpd-h1" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: EASE }}>{post.title}</motion.h1>
      <motion.div className="mpd-head-meta" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.45, ease: EASE }}>
        <Avatar who={CHELSEA} size={40} />
        <span><b>Chelsea</b> · {post.scheduled ? <>goes out {fmtWhen(post.scheduled)}</> : post.ago}{post.edited && " · edited"}</span>
        <VisBadge visibility={post.visibility} />
      </motion.div>
      <motion.p className="mpd-head-hand" aria-hidden="true" initial={{ opacity: 0, rotate: -10 }} animate={{ opacity: 1, rotate: -6 }} transition={{ delay: 0.6, duration: 0.5, ease: EASE }}>
        from my sewing table
        <svg viewBox="0 0 30 46"><Draw d={HEART} delay={0.9} width={3.2} /></svg>
      </motion.p>
    </> : <motion.h1 className="mpd-h1" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: EASE }}>Post not found</motion.h1>}
  </div>;
}

/* ─── Comments ─── */

type Reply = { thread: string; to: LiveComment } | null;

function CommentForm({ me, onSubmit, onCancel, label, placeholder, initial = "", autoFocus = false, compact = false }: {
  me: Member; onSubmit: (text: string) => void; onCancel?: () => void; label: string; placeholder: string; initial?: string; autoFocus?: boolean; compact?: boolean;
}) {
  const [text, setText] = useState(initial);
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!autoFocus) return;
    const el = ref.current;
    el?.focus();
    el?.setSelectionRange(initial.length, initial.length);
  }, [autoFocus, initial]);
  const send = (e?: FormEvent) => { e?.preventDefault(); if (!text.trim()) return; onSubmit(text.trim()); setText(""); };
  return <form className={`mpd-form${compact ? " is-compact" : ""}`} onSubmit={send}>
    <Avatar who={me} size={compact ? 32 : 42} />
    <div className="mpd-form-box">
      <label htmlFor={id} className="mp-sr">{label}</label>
      <textarea id={id} ref={ref} rows={compact ? 2 : 3} value={text} placeholder={placeholder} onChange={e => setText(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
          if (e.key === "Escape" && onCancel) onCancel();
        }} />
      <div className="mpd-form-acts">
        <span className="mpd-form-hint" aria-hidden="true">⌘ + Enter to post</span>
        {onCancel && <button type="button" className="mb-btn mb-btn-line mb-btn-sm" onClick={onCancel}>Cancel</button>}
        <button type="submit" className={`mb-btn mb-btn-ink${compact ? " mb-btn-sm" : ""}`} disabled={!text.trim()}><Send size={15} aria-hidden="true" /> {compact ? "Reply" : "Post comment"}</button>
      </div>
    </div>
  </form>;
}

function CommentItem({ c, post, thread, reply, setReply, add, depth = 0 }: {
  c: LiveComment; post: LivePost; thread: string; reply: Reply; setReply: (r: Reply) => void; add: (text: string, thread?: string) => void; depth?: number;
}) {
  const role = useRole();
  const me = viewer(role);
  if (c.hidden && role !== "chelsea") return null;
  const chelsea = role === "chelsea";
  const byChelsea = c.by.badge === "chelsea";
  const mine = !!me && c.by.id === me.id;
  const patch = (fn: (x: LiveComment) => LiveComment | null) => postStore.patch(post.id, p => ({ ...p, comments: mapComment(p.comments, c.id, fn) }));
  const del = () => {
    const before = post.comments;
    patch(() => null);
    toast({ text: "Comment deleted.", action: { label: "Undo", run: () => postStore.patch(post.id, p => ({ ...p, comments: before })) } });
  };
  const report = () => {
    patch(x => ({ ...x, reported: true }));
    toast({ text: chelsea ? "Reported. It’s in your moderation queue." : "Thanks. Chelsea will take a look." });
  };
  const hide = () => {
    patch(x => ({ ...x, hidden: !x.hidden }));
    toast({ text: c.hidden ? "Comment is visible again." : "Hidden from the club. Only you can see it now.", ...(!c.hidden && { action: { label: "Undo", run: () => patch(x => ({ ...x, hidden: false })) } }) });
  };
  const openThread = reply?.thread === c.id && depth === 0;
  const replies = (c.replies ?? []).filter(r => chelsea || !r.hidden);

  return <motion.li className={`mpd-c${byChelsea ? " is-chelsea" : ""}${c.hidden ? " is-hidden" : ""}${depth ? " is-reply" : ""}`} layout="position"
    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.35, ease: EASE }}>
    <article className="mpd-c-item" aria-label={`${byChelsea ? "Chelsea’s reply" : `Comment by ${c.by.name}`}, ${c.ago}`}>
      <Avatar who={c.by} size={depth ? 34 : 42} />
      <div className="mpd-c-main">
        <div className="mpd-c-bubble">
          <p className="mpd-c-by">
            <b>{c.by.name}</b>
            {byChelsea && <span className="mpd-badge mpd-badge-chelsea"><Scissors size={11} aria-hidden="true" /> Creator</span>}
            {c.by.badge === "founding" && <span className="mpd-badge mpd-badge-found">{FOUNDING}</span>}
            {mine && !byChelsea && <span className="mpd-badge mpd-badge-you">you</span>}
            <span className="mpd-c-ago">{c.ago}</span>
          </p>
          {c.hidden && <p className="mpd-c-flag"><EyeOff size={13} aria-hidden="true" /> Hidden from the club. Only you can see it.</p>}
          <p className="mpd-c-text">{c.text}</p>
          {c.reported && <p className="mpd-c-flag"><Flag size={13} aria-hidden="true" /> {chelsea ? "Reported for review" : "You reported this"}</p>}
        </div>
        <div className="mpd-c-acts">
          <LikeButton small what="comment" liked={c.liked} count={c.likes} onToggle={() => patch(toggleLike)} />
          {me && <button type="button" className="mpd-c-btn" aria-expanded={reply?.to.id === c.id} onClick={() => setReply(reply?.to.id === c.id ? null : { thread, to: c })}>Reply</button>}
          {chelsea && !byChelsea && <button type="button" className="mpd-c-btn" onClick={hide}>{c.hidden ? <><Eye size={14} aria-hidden="true" /> Unhide</> : <><EyeOff size={14} aria-hidden="true" /> Hide</>}</button>}
          {me && !mine && !c.reported && <button type="button" className="mpd-c-btn" onClick={report}><Flag size={14} aria-hidden="true" /> Report</button>}
          {mine && <button type="button" className="mpd-c-btn" onClick={del}><Trash2 size={14} aria-hidden="true" /> Delete</button>}
        </div>
      </div>
    </article>
    {(replies.length > 0 || openThread) && <ul className="mpd-replies">
      <AnimatePresence initial={false}>
        {replies.map(r => <CommentItem key={r.id} c={r} post={post} thread={thread} reply={reply} setReply={setReply} add={add} depth={1} />)}
      </AnimatePresence>
      {openThread && me && <li className="mpd-reply-form">
        <CommentForm compact autoFocus me={me} label={`Reply to ${reply!.to.by.name}`}
          initial={reply!.to.id !== c.id && reply!.to.by.id !== me.id ? `@${reply!.to.by.name.split(" ")[0]} ` : ""}
          placeholder={`Reply to ${reply!.to.by.name.split(" ")[0]}…`} onCancel={() => setReply(null)} onSubmit={t => add(t, thread)} />
      </li>}
    </ul>}
  </motion.li>;
}

function Comments({ post }: { post: LivePost }) {
  const role = useRole();
  const me = viewer(role);
  const [reply, setReply] = useState<Reply>(null);
  const total = countComments(post, role === "chelsea");
  const add = (text: string, thread?: string) => {
    if (!me) return;
    const c: LiveComment = { id: `c-${Date.now().toString(36)}`, by: me, text, ago: "Just now", likes: 0, mine: true };
    postStore.patch(post.id, p => ({ ...p, comments: thread ? mapComment(p.comments, thread, x => ({ ...x, replies: [...(x.replies ?? []), c] })) : [c, ...p.comments] }));
    setReply(null);
    toast({ text: thread ? "Reply posted." : "Comment posted." });
  };
  return <section id="comments" className="mpd-comments" aria-labelledby="mpd-c-title">
    <div className="mpd-c-head">
      <div className="mpd-c-title">
        <Head id="mpd-c-title" hand="the chatter" word="comments" tone="blue" className="mb-headsm" />
        <em className="mpd-c-count" aria-live="polite">{total}<span className="mb-sr"> {total === 1 ? "comment" : "comments"}</span></em>
      </div>
      {role === "chelsea" && <p className="mpd-mod-note">You can hide or report any comment. Hidden ones stay visible to you only.</p>}
    </div>
    {me ? <CommentForm me={me} label="Write a comment" placeholder={role === "chelsea" ? "Reply to the club…" : "Say something nice, ask a question…"} onSubmit={t => add(t)} />
      : <div className="mpd-join">
        <MessageCircle size={20} aria-hidden="true" />
        <p><b>Want to join the conversation?</b> Thread Friends and Members can comment, like, and vote.</p>
        <JoinButton />
      </div>}
    {post.comments.length === 0
      ? <p className="mpd-c-empty">No comments yet. {me ? "Be the first to say hi!" : ""}</p>
      : <ul className="mpd-c-list">
        <AnimatePresence initial={false}>
          {post.comments.map(c => <CommentItem key={c.id} c={c} post={post} thread={c.id} reply={reply} setReply={setReply} add={add} />)}
        </AnimatePresence>
      </ul>}
  </section>;
}

/* ─── Aside ─── */

function Supplies({ post }: { post: LivePost }) {
  const href = useHref();
  const id = useId();
  const project = PROJECTS.find(p => p.id === post.projectId) ?? PROJECTS[0];
  const first = CLASSES.find(c => c.projectId === project.id);
  const [got, setGot] = useState<Set<number>>(() => new Set());
  const flip = (i: number) => setGot(s => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  return <SideCard className="mpd-supplies" labelledBy={id}>
    <i className="mp-tape mp-tape-extra" aria-hidden="true" />
    <p className="mpd-supplies-hand">shopping list</p>
    <h2 id={id} className="mp-side-h">{project.title} supplies</h2>
    <ul>
      {project.supplies.map((s, i) => <li key={s}><label><input type="checkbox" checked={got.has(i)} onChange={() => flip(i)} /><span>{s}</span></label></li>)}
    </ul>
    <p className="mpd-supplies-count" aria-live="polite">{got.size === project.supplies.length ? "All set. See you in class!" : `${got.size} of ${project.supplies.length} gathered`}</p>
    {project.pattern && <p className="mpd-supplies-pattern">Pattern: {project.pattern}</p>}
    {first && <Link className="mb-btn mb-btn-ink mb-btn-sm" to={href(M.klass(first.id))}>Go to class 1 <ArrowRight size={16} aria-hidden="true" /></Link>}
  </SideCard>;
}

function Chapters({ post }: { post: LivePost }) {
  const href = useHref();
  const id = useId();
  const k = CLASSES.find(c => c.id === post.classId);
  if (!k || !hasReplay(k)) return null;
  return <SideCard className="mpd-chapters" labelledBy={id}>
    <p className="mp-side-kicker">{k.part} · {k.length}</p>
    <h2 id={id} className="mp-side-h">Jump to a chapter</h2>
    <ol>
      {k.chapters.map(ch => <li key={ch.t}><Link to={`${href(M.klass(k.id))}&t=${encodeURIComponent(ch.t)}`}><b>{ch.t}</b><span>{ch.label}</span><Play size={13} fill="currentColor" aria-hidden="true" /></Link></li>)}
    </ol>
  </SideCard>;
}

/* ─── Previous / next ─── */

function Neighbors({ post, list }: { post: LivePost; list: LivePost[] }) {
  const role = useRole();
  const href = useHref();
  const i = list.findIndex(p => p.id === post.id);
  if (i < 0) return null;
  const card = (p: LivePost | undefined, dir: "prev" | "next") => p
    ? <Link className={`mpd-nav-card is-${dir}`} to={href(M.post(p.id))}>
      <small>{dir === "prev" ? <><ArrowLeft size={14} aria-hidden="true" /> Previous post</> : <>Next post <ArrowRight size={14} aria-hidden="true" /></>}</small>
      <span className="mp-kind">{KIND[p.kind]}</span>
      <b>{p.title}</b>
      {!canSee(role, p.visibility) && <VisBadge visibility={p.visibility} size="sm" className="mpd-nav-lock" />}
    </Link>
    : <span className="mpd-nav-end" aria-hidden="true">{dir === "prev" ? "you’re all caught up ♥" : "that’s the oldest one!"}</span>;
  return <nav className="mpd-nav" aria-label="More posts">
    {card(list[i - 1], "prev")}
    {card(list[i + 1], "next")}
  </nav>;
}

/* ─── Page ─── */

function Detail({ post, list }: { post: LivePost; list: LivePost[] }) {
  const role = useRole();
  const navigate = useNavigate();
  const href = useHref();
  const [editing, setEditing] = useState(false);
  const open = canSee(role, post.visibility);
  const comments = countComments(post, role === "chelsea");
  const goComments = () => document.getElementById("comments")?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });

  return <section className="mpd" aria-label="Post">
    <div className="mpd-grid">
      <div className="mpd-main">
        <article className={`mp-card mpd-post mp-kind-${post.kind}${post.pinned ? " is-pinned" : ""}`} aria-labelledby="mpd-title-sr">
          <h2 id="mpd-title-sr" className="mp-sr">{post.title}</h2>
          {post.scheduled && <p className="mpd-sched"><CalendarClock size={15} aria-hidden="true" /> Scheduled for {fmtWhen(post.scheduled)}. Only you can see it until then.</p>}
          {role === "chelsea" && !editing && <div className="mpd-tools">
            <span>Your post</span>
            <PostMenu post={post} onEdit={() => setEditing(true)} onDeleted={() => navigate(href(M.posts))} />
          </div>}
          {editing ? <EditForm post={post} onDone={() => setEditing(false)} />
            : open ? <>
              {post.body && <p className="mpd-body">{post.body}</p>}
              <Media post={post} full />
            </> : <PostLocked post={post} big />}
          <footer className="mp-card-foot">
            {open ? <>
              <LikeButton liked={post.liked} count={post.likes} onToggle={() => postStore.patch(post.id, toggleLike)} />
              <button type="button" className="mp-act" onClick={goComments}><MessageCircle size={17} aria-hidden="true" /> {comments}<span className="mp-sr"> {comments === 1 ? "comment" : "comments"}</span></button>
              <ShareButton id={post.id} />
            </> : <p className="mp-quiet">{post.likes} likes · {comments} {comments === 1 ? "comment" : "comments"} from {post.visibility === "members" ? "members" : "the club"}</p>}
          </footer>
        </article>
        {open && <Comments post={post} />}
        <Neighbors post={post} list={list} />
      </div>
      <aside className="mpd-side" aria-label="About this post">
        {open && post.kind === "project" ? <Supplies post={post} />
          : open && post.kind === "replay" && post.classId && CLASSES.some(c => c.id === post.classId && hasReplay(c)) ? <Part p="club"><Chapters post={post} /></Part>
            : <Part p="club"><RoleCard role={role} live={list} /></Part>}
        <div className="mp-side-stick"><Part p="making"><Calendar /></Part></div>
      </aside>
    </div>
  </section>;
}

function Missing({ soon }: { soon?: boolean }) {
  const href = useHref();
  return <section className="mpd mpd-missing">
    <p className="mpd-missing-hand">{soon ? "not out yet!" : "this one wandered off"}</p>
    <p>{soon ? "This post is scheduled and goes out soon." : "It may have been deleted, or the link has a typo."}</p>
    <Link className="mb-btn mb-btn-ink" to={href(M.posts)}><ArrowLeft size={16} aria-hidden="true" /> Back to all posts</Link>
  </section>;
}

export function MembersPost() {
  const { id = "" } = useParams();
  const { hash } = useLocation();
  const all = usePosts();
  const found = all.find(p => p.id === id);
  const list = published(all);

  // A fresh post opens at the top (or at #comments); the feed's scroll position doesn't carry over.
  useEffect(() => {
    const el = hash ? document.getElementById(hash.slice(1)) : null;
    if (el) requestAnimationFrame(() => el.scrollIntoView());
    else window.scrollTo(0, 0);
  }, [id, hash]);

  return <MembersPage page="post" header={<MotionConfig reducedMotion="user"><HeaderFor post={found} /></MotionConfig>}>
    <MotionConfig reducedMotion="user">
      <Part p="faves"><Body post={found} list={list} /></Part>
    </MotionConfig>
    <ToastHost />
  </MembersPage>;
}

/** Scheduled posts stay hidden from everyone but Chelsea. */
function useVisible(post?: LivePost) {
  const role = useRole();
  return post && (!post.scheduled || role === "chelsea") ? post : undefined;
}
function HeaderFor({ post }: { post?: LivePost }) { return <Header post={useVisible(post)} />; }
function Body({ post, list }: { post?: LivePost; list: LivePost[] }) {
  const shown = useVisible(post);
  return shown ? <Detail post={shown} list={list} /> : <Missing soon={!!post?.scheduled} />;
}
